import React, { Component } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import IconPlus from '../IconPlus/IconPlus';

import css from './SelectSingleFilterPlain.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const IconCheckbox = props => {
  const { selected } = props;

  return (
    <svg className={css.checkboxWrapper} width="14" height="14" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" fillRule="evenodd">
        <g transform="translate(2 2)" className={selected ? css.checked : null}>
          <path
            className={selected ? css.checked : css.checkedSuccess}
            d="M9.9992985 1.5048549l-.0194517 6.9993137C9.977549 9.3309651 9.3066522 10 8.4798526 10H1.5001008c-.8284271 0-1.5-.6715729-1.5-1.5l-.000121-7c0-.8284271.6715728-1.5 1.5-1.5h.000121l6.9993246.0006862c.8284272.000067 1.4999458.671694 1.499879 1.5001211a1.5002208 1.5002208 0 0 1-.0000059.0040476z"
          />
          <path
            className={selected ? css.box : css.boxSuccess}
            strokeWidth="2"
            d="M10.9992947 1.507634l-.0194518 6.9993137C10.9760133 9.8849417 9.8578519 11 8.4798526 11H1.5001008c-1.3807119 0-2.5-1.1192881-2.5-2.4999827L-1.0000202 1.5c0-1.3807119 1.119288-2.5 2.500098-2.5l6.9994284.0006862c1.3807118.0001115 2.4999096 1.11949 2.4997981 2.5002019-.0000018.003373-.0000018.003373-.0000096.0067458z"
          />
        </g>
        <path
          d="M5.636621 10.7824771L3.3573694 8.6447948c-.4764924-.4739011-.4764924-1.2418639 0-1.7181952.4777142-.473901 1.251098-.473901 1.7288122 0l1.260291 1.1254782 2.8256927-4.5462307c.3934117-.5431636 1.1545778-.6695372 1.7055985-.278265.5473554.3912721.6731983 1.150729.2797866 1.6951077l-3.6650524 5.709111c-.2199195.306213-.5803433.5067097-.9920816.5067097-.3225487 0-.6328797-.1263736-.8637952-.3560334z"
          fill={selected ? '#FFF' : 'transparent'}
        />
      </g>
    </svg>
  );
};

const IconDisk = props => {
  const { isSelected, isHovered } = props;

  return (
    <svg
      className={css.discWrapper}
      width="22"
      height="14"
      version="1.1"
      viewBox="0 0 3.7042 3.7042"
      xmlSpace="preserve"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="1.8451"
        cy="1.8534"
        r="1.5138"
        fill={isSelected ? "var(--marketplaceColor)" : "none"}
        stroke={isSelected || isHovered ? "var(--marketplaceColor)" : "var(--matterColorDark)"}
        strokeWidth=".3536"
      />
    </svg>
  );
};

IconDisk.defaultProps = { isSelected: null, isHovered: null };

IconDisk.propTypes = { isSelected: bool, isHovered: bool };


class SelectSingleFilterPlain extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false, isHovered: {} };
    this.selectOption = this.selectOption.bind(this);
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  selectOption(option, e) {
    const { queryParamNames, onSelect } = this.props;
    const queryParamName = getQueryParamName(queryParamNames);
    onSelect({ [queryParamName]: option });

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  toggleIsOpen() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  handleMouseEnter(optionKey) {
    this.setState(prevState => ({
      isHovered: {
        ...prevState.isHovered,
        [optionKey]: true, // Set isHovered to true for the hovered option
      },
    }));
  }

  handleMouseLeave(optionKey) {
    this.setState(prevState => ({
      isHovered: {
        ...prevState.isHovered,
        [optionKey]: false, // Set isHovered to false for the unhovered option
      },
    }));
  }

  render() {
    const {
      rootClassName,
      className,
      label,
      options,
      queryParamNames,
      initialValues,
      twoColumns,
      useBorder,
      useHighlight,
    } = this.props;

    const queryParamName = getQueryParamName(queryParamNames);
    const initialValue =
      initialValues && initialValues[queryParamName] ? initialValues[queryParamName] : null;
    const labelClass = initialValue ? css.labelSelected : css.label;

    const optionsContainerClass = classNames({
      [css.optionsContainerOpen]: this.state.isOpen,
      [css.optionsContainerClosed]: !this.state.isOpen,
      [css.twoColumns]: twoColumns,
    });

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes}>
        <div className={css.filterHeader}>
          <button className={css.labelButton} onClick={this.toggleIsOpen}>
            <span className={css.labelButtonContent}>
              <span className={labelClass}>{label}</span>
              <span className={css.openSign}>
                <IconPlus isOpen={this.state.isOpen} isSelected={!!initialValue} />
              </span>
            </span>
          </button>
        </div>
        <div className={optionsContainerClass}>
          {options.map(option => {
            // check if this option is selected
            const selected = initialValue === option.key;
            const isHovered = this.state.isHovered[option.key];
            const optionClass = classNames(css.option, {
              [css.optionHighlight]: selected && useHighlight,
            });
            // menu item selected bullet or border class
            const optionBorderClass = useBorder
              ? classNames({
                [css.optionBorderSelected]: selected,
                [css.optionBorder]: !selected,
              })
              : null;

            return (
              <button
                key={option.key}
                className={css.option}
                onClick={() => this.selectOption(option.key)}
                onMouseEnter={() => this.handleMouseEnter(option.key)}
                onMouseLeave={() => this.handleMouseLeave(option.key)}
              >
                {/* <IconCheckbox selected={selected} /> */}

                <IconDisk isSelected={selected} isHovered={isHovered} />
                {useBorder ? <span className={optionBorderClass} /> : null}
                <span
                  style={selected || isHovered ? { color: "var(--marketplaceColor)" } : null}
                >
                  {option.label}
                </span>
              </button>
            );
          })}
          <button className={css.clearButton} onClick={e => this.selectOption(null, e)}>
            <FormattedMessage id={'SelectSingleFilter.plainClear'} />
          </button>
        </div>
      </div >
    );
  }
}

SelectSingleFilterPlain.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  twoColumns: false,
  useHighlight: true,
  useBorder: false,
};

SelectSingleFilterPlain.propTypes = {
  rootClassName: string,
  className: string,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSelect: func.isRequired,

  options: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  initialValues: object,
  twoColumns: bool,
  useHighlight: bool,
  useBorder: bool,
};

export default SelectSingleFilterPlain;
